/**
 * Collection page components.
 *
 * @element facet-form     – live-filtering form; fetches and re-renders results.
 * @element facet-count    – badge showing active-filter count.
 * @element results-count  – product count display, refreshed after filter changes.
 * @element facet-remove   – anchor that removes a single active filter.
 * @element facet-sort     – sort-by listbox control.
 * @element facet-sticky   – reveals a floating "Filters" button when scrolling.
 * @element price-range    – dual-handle price range control.
 * @element infinite-button – "Load more" / infinite scroll trigger.
 * @element facets-topbar  – sticky filter bar with body-class toggle.
 * @element model-view     – toggles lifestyle-image mode on the product grid.
 * @element sub-collections – select that navigates to a sub-collection URL.
 */

// ---------------------------------------------------------------------------
// Facet Form
// ---------------------------------------------------------------------------

/**
 * Facet Form
 * Intercepts form submissions and filter changes; fetches the updated section
 * HTML and morphs the product grid, filters, and count elements.
 *
 * @element facet-form (is="form")
 * @extends HTMLFormElement
 */
if (!customElements.get('facet-form')) {
  customElements.define(
    'facet-form',
    class FacetForm extends HTMLFormElement {
      /** @type {Map<string, string>} */ #cache = new Map();
      /** @type {AbortController|null} */ #abortCtrl = null;
      #dirty = false;

      get #isMobile()       { return theme.config.mqlSmall || theme.config.isTouch; }
      get #motionReduced()  { return theme.config.motionReduced || this.hasAttribute('motion-reduced'); }

      connectedCallback() {
        this.addEventListener('change', this.#onFormChange);
        this.addEventListener('submit', this.#onFormSubmit);
      }

      disconnectedCallback() {
        this.#abortCtrl?.abort();
      }

      #onFormChange = () => {
        this.#dirty = true;
        this.dispatchEvent(new Event('submit', { cancelable: true }));
      };

      #onFormSubmit = (event) => {
        event.preventDefault();
        if (!this.#dirty) return;

        const url = this.#buildUrl().toString();
        this.renderSection(url, event);
      };

      #buildUrl() {
        const params = new URLSearchParams(new FormData(this));
        const url    = new URL(this.action);
        url.search   = '';
        params.forEach((v, k) => url.searchParams.append(k, v));

        ['page', 'filter.v.price.gte', 'filter.v.price.lte'].forEach((key) => {
          if (url.searchParams.get(key) === '') url.searchParams.delete(key);
        });

        url.searchParams.set('section_id', theme.utils.sectionId(this));
        return url;
      }

      #updateURLHash(url) {
        const clean = new URL(url);
        clean.searchParams.delete('section_id');
        history.replaceState({}, '', clean.toString());
      }

      // --- Animation helpers -----------------------------------------------

      #getParams() {
        return {
          distance:     this.#isMobile ? 30 : 50,
          duration:     this.#motionReduced ? 0.001 : (this.#isMobile ? 0.3 : 0.5),
          staggerDelay: this.#motionReduced ? 0    : (this.#isMobile ? 0.05 : 0.1),
        };
      }

      /** @param {NodeListOf<Element>} items */
      #splitByVisibility(items) {
        const vh = window.innerHeight + window.scrollY;
        const visible = [], invisible = [];
        items.forEach((item) => {
          const { top, bottom } = item.getBoundingClientRect();
          (top < vh && bottom > 0 ? visible : invisible).push(item);
        });
        return { visible, invisible };
      }

      #beforeRender() {
        const container = document.getElementById('ProductGridContainer');
        const items = container.querySelectorAll('.product-card');
        const { distance, duration, staggerDelay } = this.#getParams();
        const { visible, invisible } = this.#splitByVisibility(items);

        Motion.timeline([
          [visible,   { y: distance, opacity: 0, visibility: 'hidden' }, { duration, delay: Motion.stagger(staggerDelay) }],
          [invisible, { y: distance, opacity: 0, visibility: 'hidden' }, { duration }],
          [container, { y: distance, opacity: 0 }, { duration, easing: 'linear' }],
        ]);
        items.forEach((item) => item.style.removeProperty('transform'));

        let headerH = 0;
        if (!theme.config.mqlSmall) {
          headerH = Math.round(document.querySelector('header.header')?.clientHeight ?? 0);
        }

        setTimeout(() => {
          const target = document.querySelector('.collection');
          window.scrollTo({
            top: (target?.getBoundingClientRect().top ?? 0) + window.scrollY
              - (theme.config.isTouch || theme.config.mqlSmall ? 95 : 20)
              - headerH,
            behavior: theme.config.motionReduced ? 'auto' : 'smooth',
          });

          document.getElementById('FacetDrawer')?.classList.add('loading');
        }, 100);
      }

      #afterRender() {
        const container = document.getElementById('ProductGridContainer');
        const items = container.querySelectorAll('.product-card');
        const { distance, duration, staggerDelay } = this.#getParams();
        const { visible, invisible } = this.#splitByVisibility(items);

        Motion.timeline([
          [container, { y: [distance, 0], opacity: [0, 1] }, { duration, easing: 'linear' }],
          [visible,   { y: [distance, 0], opacity: [0, 1], visibility: ['hidden', 'visible'] }, { duration, delay: Motion.stagger(staggerDelay) }],
          [invisible, { y: [distance, 0], opacity: [0, 1], visibility: ['hidden', 'visible'] }, { duration }],
        ]);
        items.forEach((item) => item.style.removeProperty('transform'));

        document.getElementById('FacetDrawer')?.classList.remove('loading');
        document.dispatchEvent(new CustomEvent('collection:reloaded'));
      }

      // --- Section rendering -----------------------------------------------

      /**
       * @param {string} url
       * @param {Event} [event]
       */
      renderSection(url, event) {
        this.#cache.has(url)
          ? this.#renderFromCache(url, event)
          : this.#renderFromFetch(url, event);

        if (this.hasAttribute('data-history')) this.#updateURLHash(url);
        this.#dirty = false;
      }

      /**
       * @param {string} url
       * @param {Event} [event]
       */
      #renderFromFetch(url, event) {
        this.#abortCtrl?.abort();
        this.#abortCtrl = new AbortController();
        this.#beforeRender();

        const start = performance.now();

        fetch(url, { signal: this.#abortCtrl.signal })
          .then((r) => r.text())
          .then((html) => {
            const elapsed = performance.now() - start;
            setTimeout(() => {
              this.#applyResponse(html, event);
              this.#cache.set(url, html);
              this.#afterRender();
            }, elapsed > 500 ? 0 : 500);
          })
          .catch((err) => { if (err.name !== 'AbortError') console.error(err); });
      }

      /**
       * @param {string} url
       * @param {Event} [event]
       */
      #renderFromCache(url, event) {
        this.#beforeRender();
        setTimeout(() => {
          this.#applyResponse(this.#cache.get(url), event);
          this.#afterRender();
        }, 500);
      }

      /**
       * @param {string} html
       * @param {Event} [event]
       */
      #applyResponse(html, event) {
        this.#renderFilters(html, event);
        this.#renderFiltersActive(html);
        this.#renderProductGrid(html);
        this.#renderProductCount(html);
        this.#renderSortBy(html);

        theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.facetUpdate, { responseText: html });
      }

      /**
       * @param {string} html
       * @param {Event} [event]
       */
      #renderFilters(html, event) {
        const parsed  = new DOMParser().parseFromString(html, 'text/html');
        const newItems = parsed.querySelectorAll(
          '#FacetFiltersContainer [data-filter], #MobileFacetFiltersContainer [data-filter]'
        );

        const activeFilter = event?.target?.closest('[data-filter]');
        Array.from(newItems)
          .filter((el) => !activeFilter || el.getAttribute('data-index') !== activeFilter.getAttribute('data-index'))
          .forEach((el) => {
            const existing = document.querySelector(`[data-filter][data-index="${el.getAttribute('data-index')}"]`);
            if (!existing) return;

            if (existing.tagName === 'DETAILS') {
              existing.querySelector('summary + *').innerHTML = el.querySelector('summary + *')?.innerHTML ?? '';
            } else {
              existing.innerHTML = el.innerHTML;
            }
          });
      }

      /** @param {string} html */
      #renderFiltersActive(html) { this.#swap(html, 'FacetFiltersActive'); }

      /** @param {string} html */
      #renderProductGrid(html) {
        this.#swap(html, 'ProductGridContainer', (newEl) => {
          newEl.querySelector('motion-list')?.setAttribute('motion-reduced', '');
        });
      }

      /** @param {string} html */
      #renderProductCount(html) { this.#swap(html, 'ProductCount'); }

      /** @param {string} html */
      #renderSortBy(html) {
        this.#swap(html, 'SortByContainer', (newEl, oldEl) => {
          const oldSort = oldEl.querySelector('facet-sort');
          const newSort = newEl.querySelector('facet-sort');
          if (oldSort?.hasAttribute('style')) {
            newSort?.setAttribute('style', oldSort.getAttribute('style'));
          }
        });
      }

      /**
       * Replace the innerHTML of `id` with the matching element from `html`.
       * @param {string} html
       * @param {string} id
       * @param {(newEl: Element, oldEl: Element) => void} [modifier]
       */
      #swap(html, id, modifier) {
        const old = document.getElementById(id);
        if (!old) return;

        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const newEl  = parsed.getElementById(id);
        if (!newEl) return;

        modifier?.(newEl, old);
        old.innerHTML = newEl.innerHTML;
      }
    },
    { extends: 'form' }
  );
}

// ---------------------------------------------------------------------------
// Facet Count
// ---------------------------------------------------------------------------

/**
 * Facet Count
 * Badge that shows the number of active filters. Hidden when count is 0.
 *
 * @element facet-count
 */
if (!customElements.get('facet-count')) {
  customElements.define(
    'facet-count',
    class FacetCount extends HTMLElement {
      #unsubscribe = null;

      connectedCallback() {
        this.#unsubscribe = theme.pubsub.subscribe(
          theme.pubsub.PUB_SUB_EVENTS.facetUpdate,
          this.#onFacetUpdate
        );
      }

      disconnectedCallback() {
        this.#unsubscribe?.();
      }

      #onFacetUpdate = (event) => {
        const parsed = new DOMParser().parseFromString(event.responseText, 'text/html');
        const count  = parsed.querySelector('facet-count');
        if (count) {
          this.innerHTML = count.innerHTML;
          this.hidden    = parseInt(this.innerText) === 0;
        }
      };
    }
  );
}

// ---------------------------------------------------------------------------
// Results Count
// ---------------------------------------------------------------------------

/**
 * Results Count
 * Displays the number of products after filtering.
 *
 * @element results-count
 */
if (!customElements.get('results-count')) {
  customElements.define(
    'results-count',
    class ResultsCount extends HTMLElement {
      #unsubscribe = null;

      connectedCallback() {
        this.#unsubscribe = theme.pubsub.subscribe(
          theme.pubsub.PUB_SUB_EVENTS.facetUpdate,
          this.#onFacetUpdate
        );
      }

      disconnectedCallback() {
        this.#unsubscribe?.();
      }

      #onFacetUpdate = (event) => {
        const parsed = new DOMParser().parseFromString(event.responseText, 'text/html');
        const count  = parsed.querySelector('results-count');
        if (count) this.innerHTML = count.innerHTML;
      };
    }
  );
}

// ---------------------------------------------------------------------------
// Facet Remove
// ---------------------------------------------------------------------------

/**
 * Facet Remove
 * Anchor that removes a single active filter by re-fetching with the updated URL.
 *
 * @element facet-remove (is="a")
 * @extends HoverLink
 */
if (!customElements.get('facet-remove')) {
  customElements.define(
    'facet-remove',
    class FacetRemove extends HoverLink {
      connectedCallback() {
        this.addEventListener('click', this.#onClick);
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.#onClick);
      }

      #onClick = (event) => {
        const form = this.closest('form[is="facet-form"]') ?? document.querySelector('form[is="facet-form"]');
        if (!form) return;

        event.preventDefault();
        const url = new URL(this.href);
        url.searchParams.set('section_id', theme.utils.sectionId(form));
        form.renderSection(url.toString(), event);
      };
    },
    { extends: 'a' }
  );
}

// ---------------------------------------------------------------------------
// Facet Sort
// ---------------------------------------------------------------------------

/**
 * Facet Sort
 * Custom listbox-style sort-by control.
 *
 * @element facet-sort
 */
if (!customElements.get('facet-sort')) {
  customElements.define(
    'facet-sort',
    class FacetSort extends HTMLElement {
      get #listbox()   { return this.querySelector('.sort-listbox'); }
      get #selection() { return this.querySelector('.sort-selection'); }
      get #button()    { return this.querySelector('.sort-by'); }

      connectedCallback() {
        Motion.inView(this, () => {}, { margin: '200px 0px 200px 0px' });

        this.addEventListener('change', this.#onChange);
        this.#button?.addEventListener('click', this.#onToggle);
        document.addEventListener('click', this.#onDocumentClick);
      }

      disconnectedCallback() {
        this.#button?.removeEventListener('click', this.#onToggle);
        document.removeEventListener('click', this.#onDocumentClick);
      }

      #onChange = (event) => {
        const form = this.closest('form[is="facet-form"]') ?? document.querySelector('form[is="facet-form"]');
        if (form) {
          const url = new URL(window.location.href);
          url.searchParams.set('sort_by', event.target.value);
          url.searchParams.set('section_id', theme.utils.sectionId(form));
          url.searchParams.delete('page');
          form.renderSection(url.toString(), event);
        }

        if (this.#selection) {
          this.#selection.innerText = event.target.nextElementSibling?.innerText ?? '';
        }
        this.#hide();
      };

      #onToggle = () => {
        this.#button?.hasAttribute('open') ? this.#hide() : this.#show();
      };

      #onDocumentClick = (event) => {
        if (!this.contains(event.target) && this.#button?.hasAttribute('open')) {
          this.#hide();
        }
      };

      #show() { this.#button?.setAttribute('open', ''); }
      #hide() { this.#button?.removeAttribute('open'); }
    }
  );
}

// ---------------------------------------------------------------------------
// Facet Sticky
// ---------------------------------------------------------------------------

/**
 * Facet Sticky
 * Animates a floating "Filters" button in and out as the collection scrolls.
 *
 * @element facet-sticky
 */
if (!customElements.get('facet-sticky')) {
  customElements.define(
    'facet-sticky',
    class FacetSticky extends HTMLElement {
      get #button() { return this.querySelector('.button'); }

      connectedCallback() {
        const target = document.querySelector('.collection-section');
        if (!target) return;

        new IntersectionObserver(this.#onIntersect, {
          rootMargin: `-${screen.availHeight - 200}px 0px ${screen.availHeight}px 0px`,
        }).observe(target);
      }

      #onIntersect = ([entry]) => {
        if (!this.#button) return;

        if (entry.isIntersecting) {
          Motion.animate(this.#button, {
            opacity: 1, visibility: 'visible', transform: ['translateY(15px)', 'translateY(0)'],
          }, { duration: 1, easing: [0.16, 1, 0.3, 1] });
        } else {
          Motion.animate(this.#button, {
            opacity: 0, visibility: 'hidden', transform: ['translateY(0)', 'translateY(15px)'],
          }, { duration: 1, easing: [0.16, 1, 0.3, 1] });
        }
      };
    }
  );
}

// ---------------------------------------------------------------------------
// Price Range
// ---------------------------------------------------------------------------

/**
 * Price Range
 * Dual-handle slider that syncs with min/max number inputs.
 *
 * @element price-range
 */
if (!customElements.get('price-range')) {
  customElements.define(
    'price-range',
    class PriceRange extends HTMLElement {
      /** @type {HTMLInputElement} */ #rangeMin;
      /** @type {HTMLInputElement} */ #rangeMax;
      /** @type {HTMLInputElement} */ #inputMin;
      /** @type {HTMLInputElement} */ #inputMax;

      connectedCallback() {
        this.#rangeMin = this.querySelector('input[type="range"]:first-child');
        this.#rangeMax = this.querySelector('input[type="range"]:last-child');
        this.#inputMin = this.querySelector('input[name="filter.v.price.gte"]');
        this.#inputMax = this.querySelector('input[name="filter.v.price.lte"]');

        this.#inputMin.addEventListener('focus',  () => this.#inputMin.select());
        this.#inputMax.addEventListener('focus',  () => this.#inputMax.select());
        this.#inputMin.addEventListener('change', this.#onInputMinChange);
        this.#inputMax.addEventListener('change', this.#onInputMaxChange);
        this.#rangeMin.addEventListener('change', this.#onRangeMinChange);
        this.#rangeMax.addEventListener('change', this.#onRangeMaxChange);
        this.#rangeMin.addEventListener('input',  this.#onRangeMinInput);
        this.#rangeMax.addEventListener('input',  this.#onRangeMaxInput);
      }

      #onInputMinChange = (event) => {
        event.preventDefault();
        const el = event.target;
        el.value = String(Math.max(
          Math.min(parseInt(el.value), parseInt(this.#inputMax.value || el.max) - 1),
          parseInt(el.min)
        ));
        this.#rangeMin.value = el.value;
        this.#setRangeMinCSS();
      };

      #onInputMaxChange = (event) => {
        event.preventDefault();
        const el = event.target;
        el.value = String(Math.min(
          Math.max(parseInt(el.value), parseInt(this.#inputMin.value || el.min) + 1),
          parseInt(el.max)
        ));
        this.#rangeMax.value = el.value;
        this.#setRangeMaxCSS();
      };

      #onRangeMinChange = (event) => {
        event.stopPropagation();
        this.#inputMin.value = event.target.value;
        this.#inputMin.dispatchEvent(new Event('change', { bubbles: true }));
      };

      #onRangeMaxChange = (event) => {
        event.stopPropagation();
        this.#inputMax.value = event.target.value;
        this.#inputMax.dispatchEvent(new Event('change', { bubbles: true }));
      };

      #onRangeMinInput = (event) => {
        const el = event.target;
        el.value = String(Math.min(parseInt(el.value), parseInt(this.#inputMax.value || el.max) - 1));
        this.#setRangeMinCSS();
        this.#inputMin.value = el.value;
      };

      #onRangeMaxInput = (event) => {
        const el = event.target;
        el.value = String(Math.max(parseInt(el.value), parseInt(this.#inputMin.value || el.min) + 1));
        this.#setRangeMaxCSS();
        this.#inputMax.value = el.value;
      };

      #setRangeMinCSS() {
        this.#rangeMin.parentElement?.style.setProperty(
          '--range-min', `${parseInt(this.#rangeMin.value) / parseInt(this.#rangeMin.max) * 100}%`
        );
      }

      #setRangeMaxCSS() {
        this.#rangeMax.parentElement?.style.setProperty(
          '--range-max', `${parseInt(this.#rangeMax.value) / parseInt(this.#rangeMax.max) * 100}%`
        );
      }
    }
  );
}

// ---------------------------------------------------------------------------
// Infinite Button
// ---------------------------------------------------------------------------

/**
 * Infinite Button
 * "Load more" button (or auto-trigger in infinite-scroll mode) that appends
 * the next page of products to the grid.
 *
 * @element infinite-button (is="button")
 * @extends HoverButton
 */
if (!customElements.get('infinite-button')) {
  customElements.define(
    'infinite-button',
    class InfiniteButton extends HoverButton {
      /** @type {AbortController|null} */ #abortCtrl = null;

      connectedCallback() {
        this.addEventListener('click', this.#onClick);

        if (this.getAttribute('mode') === 'infinite') {
          Motion.inView(this, this.#onClick, { margin: '200px 0px 200px 0px' });
        }
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.#onClick);
        this.#abortCtrl?.abort();
      }

      #onClick = () => {
        if (this.hasAttribute('aria-busy')) return;

        this.#abortCtrl?.abort();
        this.#abortCtrl = new AbortController();

        this.#enableLoading();
        const url = this.#buildUrl().toString();

        fetch(url, { signal: this.#abortCtrl.signal })
          .then((r) => r.text())
          .then((html) => {
            this.#renderPagination(html);
            this.#renderGrid(html);
          })
          .catch((err) => { if (err.name !== 'AbortError') console.error(err); });
      };

      #buildUrl() {
        const url = new URL(this.getAttribute('action'));
        url.searchParams.set('section_id', theme.utils.sectionId(this));
        return url;
      }

      #enableLoading() {
        this.classList.add('pointer-events-none');
        this.setAttribute('aria-busy', 'true');
      }

      /** @param {string} html */
      #renderPagination(html) {
        const container = document.getElementById('ProductGridContainer');
        if (!container) return;

        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const dest   = container.querySelector('.pagination');
        const src    = parsed.querySelector('.pagination');

        src ? (dest.innerHTML = src.innerHTML) : dest?.remove();
      }

      /** @param {string} html */
      #renderGrid(html) {
        const container = document.getElementById('ProductGridContainer');
        if (!container) return;

        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const dest   = container.querySelector('motion-list');
        const src    = parsed.querySelector('motion-list');

        if (src && dest) {
          src.querySelectorAll('.card').forEach((item) => dest.appendChild(item));
          dest.reload?.();
        }
      }
    },
    { extends: 'button' }
  );
}

// ---------------------------------------------------------------------------
// Facets Topbar
// ---------------------------------------------------------------------------

/**
 * Facets Topbar
 * Adds/removes a body class when the filter bar is stuck at the top.
 *
 * @element facets-topbar
 * @extends StickyElement
 */
if (!customElements.get('facets-topbar')) {
  customElements.define(
    'facets-topbar',
    class FacetsTopbar extends StickyElement {
      positionStickySidebar() {
        const stuck = this.getBoundingClientRect().top === 0;
        document.body.classList.toggle('facets-topbar--active', stuck);
      }
    }
  );
}

// ---------------------------------------------------------------------------
// Model View
// ---------------------------------------------------------------------------

/**
 * Model View
 * Toggle between standard and lifestyle-image view modes on the product grid.
 *
 * @element model-view (is="button")
 * @extends HoverButton
 */
if (!customElements.get('model-view')) {
  customElements.define(
    'model-view',
    class ModelView extends HoverButton {
      /** @type {boolean} */ #open = false;
      /** @type {NodeListOf<HTMLElement>} */ #textSpans;

      static get observedAttributes() { return ['open']; }

      get open() { return this.#open; }
      set open(value) {
        if (value === this.#open) return;
        this.#open = value;
        value ? this.setAttribute('open', '') : this.removeAttribute('open');
      }

      connectedCallback() {
        this.#open      = this.hasAttribute('open');
        this.#textSpans = this.querySelectorAll('.link-text');

        Motion.inView(this, () => this.#restoreFromStorage(), { margin: '200px 0px 200px 0px' });
        this.addEventListener('click', this.#onClick);
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.#onClick);
      }

      attributeChangedCallback(name, _old, newVal) {
        if (name !== 'open') return;
        const active = newVal === '';
        this.#applyMode(active);
        this.#updateA11y(active);
      }

      #restoreFromStorage() {
        if (!theme.config.hasLocalStorage) return;
        const saved = window.localStorage.getItem(`${theme.settings.themeName}:model-view`);
        this.open = saved === 'true';
      }

      #onClick = (event) => {
        event.preventDefault();
        this.open = !this.open;
      };

      /** @param {boolean} active */
      #applyMode(active) {
        const container = document.getElementById('ProductGridContainer');
        container?.classList.toggle('lifestyle', active);
        container?.querySelectorAll('video-media').forEach((v) => v.pause?.());

        if (theme.config.hasLocalStorage) {
          window.localStorage.setItem(`${theme.settings.themeName}:model-view`, String(active));
        }
      }

      /** @param {boolean} active */
      #updateA11y(active) {
        if (this.#textSpans.length === 2) {
          const [defaultText, activeText] = this.#textSpans;
          this.setAttribute('aria-label', active ? activeText.textContent.trim() : defaultText.textContent.trim());
        }
      }
    },
    { extends: 'button' }
  );
}

// ---------------------------------------------------------------------------
// Sub Collections
// ---------------------------------------------------------------------------

/**
 * Sub Collections
 * Select element that navigates to the chosen collection URL.
 *
 * @element sub-collections (is="select")
 * @extends HTMLSelectElement
 */
if (!customElements.get('sub-collections')) {
  customElements.define(
    'sub-collections',
    class SubCollections extends HTMLSelectElement {
      connectedCallback() {
        this.addEventListener('change', this.#onChange);
      }

      disconnectedCallback() {
        this.removeEventListener('change', this.#onChange);
      }

      #onChange = () => {
        window.location.href = this.value;
      };
    },
    { extends: 'select' }
  );
}
